/**
 * 비밀번호 정책 검증 유틸리티 (클라이언트 측 — 서버와 동일 규칙).
 *
 * 규칙: 최소 N자(전역 보안정책 pwpol 티어 파생 — std 8·strong 12·max 14) + 영문(대소문자 포함) 1자 이상
 * + 숫자 1자 이상 + 특수문자 1자 이상. 최소 길이는 공개 API(GET /api/security-policy)의 passwordMinLength 를
 * 호출부가 전달한다(미로드 시 {@link DEFAULT_PASSWORD_MIN_LENGTH}). 최종 집행 권위는 서버(PasswordPolicy).
 * 근거: 2026-07-29-security-policy-global-keycloak-plan.md.
 *
 * 사용 방법:
 *   import { validatePassword, passwordHintsFor } from "@/lib/passwordPolicy";
 *   const { valid, errors } = validatePassword(pw, minLength);
 *   passwordHintsFor(minLength).map(...)   // 폼 체크리스트
 */
import i18next from "i18next";

/** 전역 보안정책 미로드 시 기본 최소 길이(std = 서버 PasswordPolicy.DEFAULT_MIN_LENGTH 와 동일). */
export const DEFAULT_PASSWORD_MIN_LENGTH = 8;

/** 비밀번호 정책 검증 결과 */
export interface PasswordValidationResult {
  valid: boolean;
  /** 실패한 규칙 설명 목록 (통과 시 빈 배열) */
  errors: string[];
}

/** 각 규칙: 검사 함수 + 실패 시 표시할 메시지(현재 화면 언어로 번역) */
interface PolicyRule {
  test: (pw: string) => boolean;
  message: string;
}

/** 최소 길이를 적용한 정책 규칙 목록(길이 규칙만 정책 의존, 나머지는 고정). */
function rulesFor(minLength: number): PolicyRule[] {
  return [
    {
      test: (pw) => pw.length >= minLength,
      message: i18next.t("passwordPolicy.minLength", { count: minLength }),
    },
    { test: (pw) => /[a-zA-Z]/.test(pw), message: i18next.t("passwordPolicy.letter") },
    { test: (pw) => /[0-9]/.test(pw), message: i18next.t("passwordPolicy.digit") },
    { test: (pw) => /[^a-zA-Z0-9]/.test(pw), message: i18next.t("passwordPolicy.special") },
  ];
}

/**
 * 비밀번호 정책 검증.
 * @param pw 검사할 비밀번호 문자열
 * @param minLength 전역 보안정책 최소 길이(미지정 시 {@link DEFAULT_PASSWORD_MIN_LENGTH})
 * @returns valid = 모든 규칙 통과 여부, errors = 실패 규칙 메시지 배열
 */
export function validatePassword(
  pw: string,
  minLength: number = DEFAULT_PASSWORD_MIN_LENGTH,
): PasswordValidationResult {
  const errors = rulesFor(minLength)
    .filter((r) => !r.test(pw))
    .map((r) => r.message);
  return { valid: errors.length === 0, errors };
}

/**
 * 정책 힌트 목록 — 폼의 체크리스트 표시용(입력값에 따라 통과/미통과 표시).
 * @param minLength 전역 보안정책 최소 길이(미지정 시 {@link DEFAULT_PASSWORD_MIN_LENGTH})
 */
export function passwordHintsFor(
  minLength: number = DEFAULT_PASSWORD_MIN_LENGTH,
): { message: string; test: (pw: string) => boolean }[] {
  return rulesFor(minLength).map((r) => ({ message: r.message, test: r.test }));
}
